import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle2, Loader2, Upload, Trash2, Edit } from "lucide-react";
import { readingService } from "@/services/readingService";
import type { ReadingArticle } from "@/services/readingService";

export default function AdminReading() {
  const { t } = useTranslation();
  const [jsonInput, setJsonInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [articles, setArticles] = useState<ReadingArticle[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(true);

  const fetchArticles = async () => {
    setLoadingArticles(true);
    try {
      const data = await readingService.getArticles();
      setArticles(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleImport = async () => {
    setErrorMsg("");
    setSuccessMsg("");
    if (!jsonInput.trim()) {
      setErrorMsg("O campo JSON não pode estar vazio.");
      return;
    }

    try {
      const parsed = JSON.parse(jsonInput);
      
      // Validação básica
      if (!parsed.title || !parsed.slug || !parsed.content) {
        throw new Error("O JSON precisa conter pelo menos 'title', 'slug' e 'content'.");
      }

      setLoading(true);
      const result = await readingService.importArticle({
        title: parsed.title,
        slug: parsed.slug,
        category: parsed.category || "General",
        level: parsed.level || "B1",
        estimated_time: parsed.estimated_time || "5 min",
        content: parsed.content,
        quiz: parsed.quiz || []
      });

      if (result.success) {
        setSuccessMsg(`Artigo "${parsed.title}" importado com sucesso!`);
        setJsonInput("");
        fetchArticles(); // Refresh list
      } else {
        setErrorMsg(result.error || "Erro desconhecido ao importar o artigo.");
      }
    } catch (e: any) {
      setErrorMsg("Erro de parsing JSON: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (article: ReadingArticle) => {
    const payload = {
      title: article.title,
      slug: article.slug,
      category: article.category,
      level: article.level,
      estimated_time: article.estimated_time,
      content: article.content,
      quiz: article.quiz || []
    };
    setJsonInput(JSON.stringify(payload, null, 2));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSuccessMsg("Dados do artigo carregados para edição.");
  };

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o artigo "${title}"?`)) {
      try {
        const result = await readingService.deleteArticle(id);
        if (result.success) {
          alert("Artigo excluído com sucesso.");
          fetchArticles();
        } else {
          alert("Erro ao excluir o artigo: " + result.error);
        }
      } catch (e) {
        console.error(e);
        alert("Erro inesperado ao excluir o artigo.");
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Gerenciar - Coin Lecture</h1>
        <p className="text-slate-500 mt-2 text-lg">Adicione, edite ou remova artigos de leitura na plataforma.</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Upload className="w-5 h-5 text-indigo-500" />
            Importar / Editar Artigo
          </CardTitle>
          <CardDescription>
            Cole o JSON contendo título, slug, conteúdo (em Markdown) e quiz. O sistema irá atualizar o artigo caso o slug já exista.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          <div className="space-y-2">
            <Label>Payload JSON</Label>
            <Textarea 
              placeholder="Cole o JSON do artigo aqui..." 
              className="min-h-[300px] font-mono text-sm bg-slate-50"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
            />
          </div>

          {errorMsg && (
            <div className="p-4 rounded-lg bg-red-50 text-red-700 flex items-start gap-3 border border-red-100">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold">Erro na importação</h4>
                <p className="text-sm mt-1">{errorMsg}</p>
              </div>
            </div>
          )}

          {successMsg && (
            <div className="p-4 rounded-lg bg-emerald-50 text-emerald-700 flex items-start gap-3 border border-emerald-100">
              <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold">Sucesso</h4>
                <p className="text-sm mt-1">{successMsg}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={handleImport} 
              disabled={loading}
              className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {loading ? "Processando..." : "Salvar Artigo"}
            </Button>
          </div>

        </CardContent>
      </Card>

      <div className="space-y-4 pt-8 border-t border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900">Artigos Publicados</h2>
        {loadingArticles ? (
          <div className="py-10 text-center text-slate-500 flex flex-col items-center">
            <Loader2 className="w-8 h-8 animate-spin mb-2 text-indigo-500" />
            Carregando artigos...
          </div>
        ) : articles.length === 0 ? (
          <div className="py-10 text-center text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
            Nenhum artigo publicado ainda.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {articles.map((article) => (
              <Card key={article.id} className="border-slate-200 shadow-sm flex flex-col justify-between">
                <CardHeader className="p-4 pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg leading-tight line-clamp-2">{article.title}</CardTitle>
                  </div>
                  <CardDescription className="text-xs font-semibold uppercase mt-1">
                    {article.category} • Nível {article.level}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="p-4 pt-2 flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-slate-600 hover:text-indigo-600"
                    onClick={() => handleEdit(article)}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    onClick={() => handleDelete(article.id, article.title)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Excluir
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
